import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactQuill from 'react-quill';
import EmojiPicker from 'emoji-picker-react';
import { Inbox, Send, Reply, Trash2, Paperclip, Smile, CornerUpRight, MoreHorizontal, ChevronDown, ChevronRight } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { inquiriesAPI } from '../../api/inquiries';
import { toast } from 'react-toastify';
import 'react-quill/dist/quill.snow.css';
import './InquiryList.css';

const DRAFT_KEY = (id) => `inquiry-draft-${id}`;
const DRAFT_DEBOUNCE_MS = 500;

const InquiryList = () => {
  const { theme } = useTheme();
  const [inquiries, setInquiries] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [replyBody, setReplyBody] = useState('');
  const [replyFromEmail, setReplyFromEmail] = useState('bookings');
  const [sendingReply, setSendingReply] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [showEmoji, setShowEmoji] = useState(false);
  const [draftSaved, setDraftSaved] = useState(null);
  const draftTimerRef = useRef(null);
  const fileInputRef = useRef(null);
  const quillRef = useRef(null);
  const emojiPickerWrapRef = useRef(null);
  const [emojiPickerRect, setEmojiPickerRect] = useState(null);
  const [sectionsCollapsed, setSectionsCollapsed] = useState({ unread: false, everythingElse: false });

  useEffect(() => {
    fetchInquiries();
  }, []);

  useLayoutEffect(() => {
    if (!showEmoji) {
      setEmojiPickerRect(null);
      return;
    }
    if (!emojiPickerWrapRef.current) return;
    const rect = emojiPickerWrapRef.current.getBoundingClientRect();
    const pickerHeight = 420;
    const gap = 8;
    let top = rect.top - pickerHeight - gap;
    if (top < 8) top = 8;
    setEmojiPickerRect({ top, left: rect.left });
  }, [showEmoji]);

  useEffect(() => {
    if (!showEmoji) return;
    const onDocClick = (e) => {
      if (emojiPickerWrapRef.current?.contains(e.target)) return;
      const pickerEl = document.querySelector('.inquiry-emoji-picker-portal');
      if (pickerEl?.contains(e.target)) return;
      setShowEmoji(false);
    };
    const t = setTimeout(() => document.addEventListener('click', onDocClick), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', onDocClick);
    };
  }, [showEmoji]);

  const filtered = filter === 'all'
    ? inquiries
    : inquiries.filter(i => i.status === filter);

  const unreadInquiries = filtered.filter(i => i.status === 'New');
  const everythingElseInquiries = filtered.filter(i => i.status !== 'New');

  const selected = selectedId ? inquiries.find(i => i.id === parseInt(selectedId, 10)) : null;

  const toggleSection = (section) => {
    setSectionsCollapsed((s) => ({ ...s, [section]: !s[section] }));
  };

  const renderInquiryRow = (inq) => (
    <div
      key={inq.id}
      role="button"
      tabIndex={0}
      className={`inquiry-row-wrap ${selected?.id === inq.id ? 'selected' : ''} ${inq.status === 'New' ? 'unread' : ''}`}
      onClick={(e) => {
        if (e.target.closest('.inquiry-row-check, .inquiry-row-delete')) return;
        setReplyBody('');
        setSelectedId(inq.id);
      }}
      onKeyDown={(e) => {
        if (e.target.closest('.inquiry-row-check, .inquiry-row-delete')) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setReplyBody(''); setSelectedId(inq.id); }
      }}
    >
      <label className="inquiry-checkbox-wrap inquiry-row-check" title="Select">
        <input type="checkbox" checked={selectedIds.has(inq.id)} onChange={() => toggleSelect(inq.id)} onClick={(e) => e.stopPropagation()} />
      </label>
      <div className="inquiry-row">
        <div className="inquiry-row-avatar">{getInitials(inq.sender_name)}</div>
        <div className="inquiry-row-content">
          <div className="inquiry-row-top">
            <span className="inquiry-row-name">{inq.sender_name || 'Unknown'}</span>
            <span className="inquiry-row-date">{new Date(inq.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
          </div>
          <div className="inquiry-row-preview">
            {(inq.message || '').substring(0, 60)}{(inq.message || '').length > 60 ? '…' : ''}
          </div>
        </div>
        <span className={`inquiry-row-badge ${(inq.status || 'New').replace(/\s/g, '-').toLowerCase()}`}>{inq.status || 'New'}</span>
      </div>
      <button type="button" className="inquiry-row-delete" onClick={(e) => { e.stopPropagation(); handleDelete(inq.id); }} disabled={deleting} title="Delete">
        <Trash2 size={14} />
      </button>
    </div>
  );

  const fetchInquiries = async () => {
    try {
      const data = await inquiriesAPI.getAll();
      setInquiries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load inquiries');
      setInquiries([]);
    } finally {
      setLoading(false);
    }
  };

  const saveDraftToStorage = useCallback((id, body, fromEmail) => {
    if (!id) return;
    try {
      localStorage.setItem(DRAFT_KEY(id), JSON.stringify({ body: body || '', fromEmail: fromEmail || 'bookings', updatedAt: Date.now() }));
    } catch (_) {}
  }, []);

  const loadDraftFromStorage = useCallback((id) => {
    if (!id) return null;
    try {
      const raw = localStorage.getItem(DRAFT_KEY(id));
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (_) {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    const stored = loadDraftFromStorage(selectedId);
    if (stored?.body) {
      setReplyBody(stored.body);
      if (stored.fromEmail) setReplyFromEmail(stored.fromEmail);
    } else {
      inquiriesAPI.getDraft(selectedId).then(({ body, fromEmail }) => {
        if (body) setReplyBody(body);
        if (fromEmail) setReplyFromEmail(fromEmail);
      }).catch(() => {});
    }
    setAttachments([]);
  }, [selectedId, loadDraftFromStorage]);

  useEffect(() => {
    if (!selectedId) return;
    draftTimerRef.current = setTimeout(() => {
      saveDraftToStorage(selectedId, replyBody, replyFromEmail);
      setDraftSaved(Date.now());
    }, DRAFT_DEBOUNCE_MS);
    return () => clearTimeout(draftTimerRef.current);
  }, [selectedId, replyBody, replyFromEmail, saveDraftToStorage]);

  const updateStatus = async (id, newStatus) => {
    try {
      await inquiriesAPI.updateStatus(id, newStatus);
      fetchInquiries();
      toast.success('Status updated');
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleReply = async () => {
    if (!selected) return;
    const text = replyBody.replace(/<[^>]*>/g, ' ').trim();
    if (!text) return;
    setSendingReply(true);
    try {
      if (attachments.length > 0) {
        const fd = new FormData();
        fd.append('message', replyBody);
        fd.append('fromEmail', replyFromEmail);
        attachments.forEach((f) => fd.append('attachments', f));
        await inquiriesAPI.sendReplyWithAttachments(selected.id, fd);
      } else {
        await inquiriesAPI.sendReply(selected.id, replyBody, replyFromEmail);
      }
      setReplyBody('');
      setAttachments([]);
      localStorage.removeItem(DRAFT_KEY(selected.id));
      try { await inquiriesAPI.saveDraft(selected.id, '', replyFromEmail); } catch (_) {}
      fetchInquiries();
      toast.success('Reply sent');
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || err.message;
      toast.error(msg || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!selected) return;
    try {
      await inquiriesAPI.saveDraft(selected.id, replyBody, replyFromEmail);
      saveDraftToStorage(selected.id, replyBody, replyFromEmail);
      toast.success('Draft saved');
    } catch (err) {
      toast.error('Failed to save draft');
    }
  };

  const handleDiscard = () => {
    if (!selected) return;
    setReplyBody('');
    setAttachments([]);
    localStorage.removeItem(DRAFT_KEY(selected.id));
    try { inquiriesAPI.saveDraft(selected.id, '', replyFromEmail); } catch (_) {}
    toast.info('Draft discarded');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    setDeleting(true);
    try {
      await inquiriesAPI.delete(id);
      if (selectedId === id) setSelectedId(null);
      setSelectedIds((s) => { const n = new Set(s); n.delete(id); return n; });
      fetchInquiries();
      toast.success('Inquiry deleted');
    } catch (err) {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} inquiry(ies)?`)) return;
    setDeleting(true);
    try {
      await inquiriesAPI.bulkDelete(ids);
      if (selectedId && ids.includes(parseInt(selectedId, 10))) setSelectedId(null);
      setSelectedIds(new Set());
      fetchInquiries();
      toast.success('Inquiries deleted');
    } catch (err) {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((i) => i.id)));
  };

  const addEmoji = (emojiData) => {
    const editor = quillRef.current?.getEditor?.();
    if (editor) {
      const sel = editor.getSelection();
      const len = editor.getLength();
      const pos = sel ? sel.index : Math.max(0, len - 1);
      editor.insertText(pos, emojiData.emoji);
      editor.setSelection(pos + 1);
      editor.focus();
    } else {
      setReplyBody((prev) => prev + emojiData.emoji);
    }
    setShowEmoji(false);
  };

  const onFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments((prev) => [...prev, ...files].slice(0, 5));
    e.target.value = '';
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const counts = {
    all: inquiries.length,
    New: inquiries.filter(i => i.status === 'New').length,
    'In Progress': inquiries.filter(i => i.status === 'In Progress').length,
    Replied: inquiries.filter(i => i.status === 'Replied').length,
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(/\s+/).map(n => n[0]).slice(0, 2).join('').toUpperCase();
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      ['link', 'clean'],
    ],
  };

  const quillFormats = ['header', 'bold', 'italic', 'underline', 'strike', 'color', 'background', 'list', 'bullet', 'indent', 'link'];

  const filters = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'New', label: 'New', count: counts.New },
    { key: 'In Progress', label: 'In Progress', count: counts['In Progress'] },
    { key: 'Replied', label: 'Replied', count: counts.Replied },
  ];

  if (loading) {
    return (
      <motion.div className="inquiry-list-loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="inquiry-loading-spinner" />
        <p>Loading inquiries...</p>
      </motion.div>
    );
  }

  return (
    <motion.div className="inquiry-list-container" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="inquiry-list-header">
        <div className="inquiry-header-content">
          <div className="inquiry-header-icon-wrap">
            <Inbox size={28} className="inquiry-header-icon" />
          </div>
          <div>
            <h1>Inquiries</h1>
            <p>Manage contact form submissions</p>
          </div>
        </div>
      </div>

      <div className="inquiry-filters">
        {filters.map(({ key, label, count }) => (
          <button key={key} className={`inquiry-filter-pill ${filter === key ? 'active' : ''}`} onClick={() => setFilter(key)}>
            <span className="filter-label">{label}</span>
            <span className="filter-count">{count}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <motion.div className="inquiry-empty" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}>
          <div className="inquiry-empty-icon"><Inbox size={48} strokeWidth={1.5} /></div>
          <p>No inquiries found.</p>
          <span className="inquiry-empty-hint">Try selecting a different filter</span>
        </motion.div>
      ) : (
        <div className="inquiry-split">
          <div className="inquiry-master">
            <div className="inquiry-master-toolbar">
              <label className="inquiry-checkbox-wrap inquiry-checkbox-select-all" title="Select all">
                <input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0} onChange={toggleSelectAll} />
              </label>
              {selectedIds.size > 0 && (
                <button type="button" className="inquiry-bulk-delete" onClick={handleBulkDelete} disabled={deleting}>
                  <Trash2 size={16} /> Delete ({selectedIds.size})
                </button>
              )}
            </div>
            {(unreadInquiries.length > 0 || everythingElseInquiries.length > 0) ? (
              <>
                {unreadInquiries.length > 0 && (
                  <div className="inquiry-section">
                    <div
                      role="button"
                      tabIndex={0}
                      className="inquiry-section-header"
                      onClick={() => toggleSection('unread')}
                      onKeyDown={(e) => e.key === 'Enter' && toggleSection('unread')}
                      aria-expanded={!sectionsCollapsed.unread}
                    >
                      {sectionsCollapsed.unread ? (
                        <ChevronRight size={18} className="inquiry-section-chevron" />
                      ) : (
                        <ChevronDown size={18} className="inquiry-section-chevron" />
                      )}
                      <span className="inquiry-section-title">Unread</span>
                      <span className="inquiry-section-count">
                        1–{Math.min(unreadInquiries.length, 50)} of {unreadInquiries.length}
                      </span>
                      <button type="button" className="inquiry-section-more" onClick={(e) => e.stopPropagation()} aria-label="More options">
                        <MoreHorizontal size={18} />
                      </button>
                    </div>
                    {!sectionsCollapsed.unread && (
                      <div className="inquiry-section-rows">
                        {unreadInquiries.map(renderInquiryRow)}
                      </div>
                    )}
                  </div>
                )}
                {everythingElseInquiries.length > 0 && (
                  <div className="inquiry-section">
                    <div
                      role="button"
                      tabIndex={0}
                      className="inquiry-section-header"
                      onClick={() => toggleSection('everythingElse')}
                      onKeyDown={(e) => e.key === 'Enter' && toggleSection('everythingElse')}
                      aria-expanded={!sectionsCollapsed.everythingElse}
                    >
                      {sectionsCollapsed.everythingElse ? (
                        <ChevronRight size={18} className="inquiry-section-chevron" />
                      ) : (
                        <ChevronDown size={18} className="inquiry-section-chevron" />
                      )}
                      <span className="inquiry-section-title">Everything else</span>
                      <span className="inquiry-section-count">
                        1–{Math.min(everythingElseInquiries.length, 50)} of {everythingElseInquiries.length}
                      </span>
                      <button type="button" className="inquiry-section-more" onClick={(e) => e.stopPropagation()} aria-label="More options">
                        <MoreHorizontal size={18} />
                      </button>
                    </div>
                    {!sectionsCollapsed.everythingElse && (
                      <div className="inquiry-section-rows">
                        {everythingElseInquiries.map(renderInquiryRow)}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : null}
          </div>

          <div className="inquiry-detail">
            {selected ? (
              <AnimatePresence mode="wait">
                <motion.div key={selected.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} className="inquiry-detail-inner">
                  <div className="inquiry-message-card">
                    <div className="inquiry-detail-header">
                      <h2 className="inquiry-detail-subject">{selected.source === 'photograph-booking' ? 'Photography Booking Request' : 'Inquiry'}</h2>
                      <div className="inquiry-detail-meta">
                        <div className="inquiry-detail-sender">
                          <div className="inquiry-detail-avatar">{getInitials(selected.sender_name)}</div>
                          <div className="inquiry-detail-sender-info">
                            <span className="inquiry-detail-sender-name">{selected.sender_name || 'Unknown'}</span>
                            <a href={`mailto:${selected.email}`} className="inquiry-detail-email">&lt;{selected.email}&gt;</a>
                            <span className="inquiry-detail-to">to you</span>
                          </div>
                        </div>
                        <div className="inquiry-detail-meta-right">
                          <span className="inquiry-detail-date">
                            {new Date(selected.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <div className="inquiry-detail-action-icons">
                            <button type="button" className="inquiry-detail-icon-btn" title="Reply" aria-label="Reply"><Reply size={18} /></button>
                            <button type="button" className="inquiry-detail-icon-btn" title="More" aria-label="More"><MoreHorizontal size={18} /></button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {(selected.phone || selected.event_type || selected.event_date || selected.location) && (
                      <div className="inquiry-detail-booking">
                        {selected.phone && <p><strong>Phone:</strong> {selected.phone}</p>}
                        {selected.event_type && <p><strong>Event type:</strong> {selected.event_type}</p>}
                        {selected.event_date && <p><strong>Event date:</strong> {new Date(selected.event_date).toLocaleDateString()}</p>}
                        {selected.location && <p><strong>Location:</strong> {selected.location}</p>}
                      </div>
                    )}

                    <div className="inquiry-detail-body">{selected.message || '—'}</div>

                    <div className="inquiry-detail-footer-actions">
                      <div className="inquiry-detail-action-buttons">
                        <span className="inquiry-detail-pill inquiry-detail-pill-reply">
                          <Reply size={16} />
                          Reply
                        </span>
                        <span className="inquiry-detail-pill inquiry-detail-pill-forward">
                          <CornerUpRight size={16} />
                          Forward
                        </span>
                        <span className="inquiry-detail-pill inquiry-detail-pill-emoji" title="Insert emoji" aria-label="Insert emoji">
                          <Smile size={18} />
                        </span>
                      </div>
                      <select value={selected.status || 'New'} onChange={(e) => updateStatus(selected.id, e.target.value)} className="inquiry-status-select">
                        <option value="New">New</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Replied">Replied</option>
                      </select>
                    </div>
                  </div>

                  <div className="inquiry-reply-compose">
                    <div className="inquiry-reply-header">
                      <Reply size={18} />
                      <span>Reply to {selected.email}</span>
                      <select value={replyFromEmail} onChange={(e) => setReplyFromEmail(e.target.value)} className="inquiry-reply-from-select" title="Send from">
                        <option value="bookings">From: bookings@itskasimproduction.com</option>
                        <option value="info">From: info@itskasimproduction.com</option>
                      </select>
                    </div>

                    <div className="inquiry-reply-editor-wrap">
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={replyBody}
                        onChange={setReplyBody}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Type your reply here..."
                        className="inquiry-quill-editor"
                      />
                      <div className="inquiry-reply-extra-toolbar">
                        <div className="inquiry-reply-toolbar-group" ref={emojiPickerWrapRef}>
                          <button type="button" className="inquiry-toolbar-btn" onClick={() => setShowEmoji((s) => !s)} title="Insert emoji">
                            <Smile size={22} />
                          </button>
                          <button type="button" className="inquiry-toolbar-btn" onClick={() => fileInputRef.current?.click()} title="Attach files">
                            <Paperclip size={22} />
                          </button>
                          <input ref={fileInputRef} type="file" multiple accept="*/*" onChange={onFileChange} style={{ display: 'none' }} />
                        </div>
                        {draftSaved && <span className="inquiry-draft-indicator">Draft saved</span>}
                      </div>
                    </div>
                    {showEmoji && emojiPickerRect && ReactDOM.createPortal(
                      <div
                        className="inquiry-emoji-picker-portal"
                        style={{
                          position: 'fixed',
                          top: emojiPickerRect.top,
                          left: emojiPickerRect.left,
                          zIndex: 9999,
                        }}
                      >
                        <div className="inquiry-emoji-picker">
                          <EmojiPicker
                            onEmojiClick={addEmoji}
                            theme={theme}
                            emojiStyle="google"
                            searchPlaceholder="Search"
                            suggestedEmojisMode="recent"
                            width={360}
                            height={420}
                            previewConfig={{ showPreview: false }}
                            className="inquiry-emoji-picker-popup"
                          />
                        </div>
                      </div>,
                      document.body
                    )}

                    {attachments.length > 0 && (
                      <div className="inquiry-attachments">
                        {attachments.map((f, i) => (
                          <span key={i} className="inquiry-attachment-chip">
                            {f.name}
                            <button type="button" onClick={() => removeAttachment(i)}>&times;</button>
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="inquiry-reply-footer">
                      <button type="button" className="inquiry-send-btn" onClick={handleReply} disabled={sendingReply || !replyBody?.replace(/<[^>]*>/g, ' ').trim()}>
                        <Send size={18} />
                        {sendingReply ? 'Sending...' : 'Send'}
                      </button>
                      <button type="button" className="inquiry-draft-btn" onClick={handleSaveDraft}>Save as Draft</button>
                      <button type="button" className="inquiry-discard-btn" onClick={handleDiscard} title="Discard">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            ) : (
              <div className="inquiry-detail-empty">
                <p className="inquiry-detail-empty-title">No conversations selected</p>
                <span className="inquiry-detail-empty-hint">Select a conversation from the list</span>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default InquiryList;
